/** Which organizations a template reads - the connector library's
 *  organization tree (item 65A: utils/orgTree, its rows and look): ticking an
 *  organization ticks everything under it, a branch shows "2 of 5", search
 *  keeps a match's place in the tree, Select all / Clear, and how many are
 *  chosen. */
import { useMemo, useState, type CSSProperties } from 'react';
import { Checkbox, Input } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useLanguage } from '@/hooks/useLanguage';
import {
  branchCount, buildTree, check, flatten, search, toggle, type TreeNode, type TreeOrg,
} from '@modules/connector_library/utils/orgTree';
import type { TenantNode } from '../../types/insights';
import '@modules/connector_library/components/LibraryOrganizations.css';

interface Props {
  tenantTree: TenantNode[];
  value: string[];
  onChange: (ids: string[]) => void;
  /** Names the tree for a screen reader and the search for a label. */
  label: string;
}

export function OrganizationPicker({ tenantTree, value, onChange, label }: Props) {
  const { t } = useLanguage();
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const tree = useMemo(() => buildTree<TreeOrg>(tenantTree.map((n) => ({
    tenant_id: n.tenant_id, parent_id: n.parent_tenant_id, name: n.tenant_name,
  }))), [tenantTree]);
  const all = useMemo(() => flatten(tree), [tree]);
  const chosen = useMemo(() => new Set(value), [value]);
  const found = useMemo(() => search(tree, query), [tree, query]);
  // Roots are open; below them a branch opens on its caret or on a search.
  const open = (node: TreeNode) => node.depth === 0 || expanded.has(node.org.tenant_id)
    || found.open.has(node.org.tenant_id);
  const shown: TreeNode[] = [];
  const walk = (nodes: TreeNode[]) => nodes.forEach((node) => {
    if (!found.visible.has(node.org.tenant_id)) return;
    shown.push(node);
    if (open(node)) walk(node.children);
  });
  walk(tree);

  return (
    <div className="tpl-picker" data-testid="template-org-picker">
      <div className="cxl-orgs__tools">
        <Input className="cxl-orgs__search" size="small" allowClear dir="auto" value={query}
               prefix={<SearchOutlined aria-hidden />} onChange={(e) => setQuery(e.target.value)}
               placeholder={t('connectorLibrary.orgs.search')} aria-label={t('connectorLibrary.orgs.search')} />
        <button type="button" className="cx-btn cx-btn--sm" onClick={() => onChange(all.map((n) => n.org.tenant_id))}
                disabled={chosen.size === all.length}>{t('connectorLibrary.orgs.selectAll')}</button>
        <button type="button" className="cx-btn cx-btn--sm" onClick={() => onChange([])} disabled={!chosen.size}>
          {t('connectorLibrary.orgs.clear')}
        </button>
      </div>
      <p className="tpl-picker__count" aria-live="polite" data-testid="template-org-count">
        {t('insights.tpl.editor.orgsChosen', { count: chosen.size, total: all.length })}
      </p>
      {query.trim() && found.visible.size === 0 ? (
        <p className="tpl-muted">{t('connectorLibrary.orgs.noMatch')}</p>
      ) : (
        <ul className="cxl-orgs__tree tpl-picker__list" role="tree" aria-label={label} aria-multiselectable="true">
          {shown.map((node) => {
            const state = check(node, chosen);
            const count = branchCount(node, chosen);
            const hasKids = node.children.length > 0;
            const isOpen = open(node);
            return (
              <li key={node.org.tenant_id} className={`cxl-orgs__row${found.matches.has(node.org.tenant_id) ? ' is-match' : ''}`}
                  role="treeitem" aria-level={node.depth + 1} aria-expanded={hasKids ? isOpen : undefined}
                  aria-selected={state === 'checked'} style={{ '--depth': node.depth } as CSSProperties}>
                <span className="cxl-orgs__caret-slot">
                  {hasKids && node.depth > 0 && (
                    <button type="button" className={`cxl-orgs__caret${isOpen ? ' is-open' : ''}`}
                            onClick={() => setExpanded((prev) => {
                              const next = new Set(prev);
                              if (next.has(node.org.tenant_id)) next.delete(node.org.tenant_id);
                              else next.add(node.org.tenant_id);
                              return next;
                            })}
                            aria-label={t(isOpen ? 'connectorLibrary.orgs.collapse' : 'connectorLibrary.orgs.expand',
                              { name: node.org.name })}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
                           strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9 6l6 6-6 6" /></svg>
                    </button>
                  )}
                </span>
                <Checkbox checked={state === 'checked'} indeterminate={state === 'partial'}
                          onChange={() => onChange([...toggle(node, chosen)])} className="cxl-orgs__check"
                          data-testid={`template-org-${node.org.tenant_id}`}>
                  <bdi dir="auto" className="cxl-orgs__name">{node.org.name}</bdi>
                </Checkbox>
                <span className="cxl-orgs__facts">
                  {hasKids && (
                    <span className="cxl-orgs__branch">
                      {t('connectorLibrary.orgs.branch', { count: count.chosen, total: count.total })}
                    </span>
                  )}
                </span>
                <span />
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
